// WebKit before Safari 26.4 reports an element's client rects divided by its
// cumulative CSS zoom (WebKit bug 185034): inside the presentation stage's
// zoomed body (ADR-0008) every rect is in the body's own px, not the screen's.
// The drag library writes such a rect as the lifted copy's position and size on
// `body`, outside the zoom, so on an iPad at 67% the copy was drawn 1.5× too far
// down and right of the finger, and 1.5× too big. Standard engines (Chrome 128+,
// Firefox 126+, Safari 26.4+) return screen px; there this installs nothing.

const PROBE_ZOOM = 0.5
const PROBE_WIDTH = 100

/**
 * Makes `getBoundingClientRect` and `getClientRects` return screen px on engines
 * that divide them by the CSS zoom. Returns whether it had to. Takes the document
 * so a test can hand it a fake engine.
 */
export function installCssZoomRects(doc = globalThis.document) {
  if (!doc) return false
  const win = doc.defaultView
  const probe = doc.createElement('div')
  probe.style.cssText = `position:absolute;visibility:hidden;zoom:${PROBE_ZOOM};width:${PROBE_WIDTH}px`
  doc.documentElement.appendChild(probe)
  const unzoomed = probe.getBoundingClientRect().width === PROBE_WIDTH
  probe.remove()
  if (!unzoomed) return false

  const cumulativeZoom = target => {
    let zoom = 1
    for (let el = target; el; el = el.parentElement) zoom *= parseFloat(win.getComputedStyle(el).zoom) || 1
    return zoom
  }
  const scaled = (rect, zoom) => (zoom === 1
    ? rect
    : new win.DOMRect(rect.x * zoom, rect.y * zoom, rect.width * zoom, rect.height * zoom))

  const { getBoundingClientRect, getClientRects } = win.Element.prototype
  win.Element.prototype.getBoundingClientRect = function () {
    return scaled(getBoundingClientRect.call(this), cumulativeZoom(this))
  }
  win.Element.prototype.getClientRects = function () {
    const zoom = cumulativeZoom(this)
    const rects = getClientRects.call(this)
    return zoom === 1 ? rects : Array.from(rects, rect => scaled(rect, zoom))
  }
  return true
}
